import { ApiError, getEventReport } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { EventCloseReportResponse } from '@jq4race/shared';
import { formatAvgSeconds, formatCents, Skeleton, StatCard } from '@jq4race/ui';
import { TriangleAlert } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { formatDateTime } from '../../../../shared/format/format-date';

// S6 — Close report (R6.2/R6.5). Only mounted from the "Report" tab, which
// EditEventPage only renders once `event.status === 'closed'` — this
// component NEVER fires before close (R6.5: the report is a point-in-time
// snapshot, generated only at close; a pre-close read 409s
// `event_report_not_available` on the backend).

interface EventReportSectionProps {
  eventId: string;
  currency: string;
}

export function EventReportSection({ eventId, currency }: EventReportSectionProps) {
  const { t, i18n } = useTranslation('events');
  const [report, setReport] = useState<EventCloseReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getEventReport(eventId);
      setReport(result);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? translateApiError(e, t, { fallback: t('eventReportSection.loadError') })
          : t('eventReportSection.loadError'),
      );
    } finally {
      setLoading(false);
    }
  }, [eventId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-28 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
      >
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
        <p className="flex-1 leading-snug">{error}</p>
      </div>
    );
  }

  if (!report) return null;

  return (
    <div className="space-y-4">
      {report.hasOutstandingPayments && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-sm border border-jq-warning/40 bg-jq-warning/10 px-4 py-3 text-[13px] text-jq-warning"
        >
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
          <p className="flex-1 leading-snug">
            {t('eventReportSection.outstandingPayments', {
              count: report.unsettledTicketIds.length,
            })}
          </p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          size="sm"
          label={t('eventReportSection.revenue')}
          value={formatCents(report.revenueTotalCents, currency)}
        />
        <StatCard size="sm" label={t('eventReportSection.volume')} value={String(report.volume)} />
        <StatCard
          size="sm"
          label={t('eventReportSection.avgRetrievalTime')}
          value={
            report.averageRetrievalTimeSeconds !== null
              ? formatAvgSeconds(report.averageRetrievalTimeSeconds)
              : '—'
          }
        />
        <StatCard
          size="sm"
          label={t('eventReportSection.capacityVariance')}
          value={
            report.expectedCapacity !== null && report.capacityVarianceCount !== null
              ? `${formatVariance(report.capacityVarianceCount)} (${report.volume}/${report.expectedCapacity})`
              : '—'
          }
        />
      </div>

      <p className="text-[12px] text-jq-graphite">
        {t('eventReportSection.generatedAt', {
          date: formatDateTime(report.generatedAt, i18n.language),
        })}
      </p>
    </div>
  );
}

// ── Formatting helpers ───────────────────────────────────────────────────────

function formatVariance(count: number): string {
  return count > 0 ? `+${count}` : String(count);
}
